/**
 * The final print: one pass that turns four separately-authored correct
 * elements into one photograph.
 *
 * Everything upstream of this filter is *locally* right — the cards are lit
 * against `LIGHT`, the felt has its own specular, the bloom picks out gold and
 * violet — but nothing has ever told them they were shot on the same stock
 * through the same lens. That is what this does, and it is deliberately quiet:
 *
 * 1. **A filmic curve.** A gentle shoulder in linear light (extended Reinhard,
 *    unit slope at black, `uShoulder` mapping to 1.0) so highlights roll off
 *    instead of clipping, then a toe-and-shoulder S in display space. The S is
 *    a blend toward `smoothstep`, which is anchored exactly at 0 and 1 and has
 *    slope `1 - uContrast` at black rather than zero — a power-law toe would
 *    crush the plum-black felt (0x1B1030, a display value of ~0.10) into flat
 *    black, which is the one thing this scene cannot afford. Measured against
 *    the palette, the felt keeps 96% of its luminance and its separation from
 *    `void` behind it; white lands at 0.96 and warm.
 * 2. **Split toning.** Highlights warm toward the key light, shadows cool
 *    toward the fill — the *same* two colours the card shader and the felt are
 *    lit with, normalised to luma 1 so they shift hue without shifting
 *    exposure. A frame graded toward its own light sources reads as one room.
 * 3. **Saturation shaping.** A touch of saturation out of the darks so the felt
 *    reads rich rather than muddy, a touch back into the highlights. The ramp
 *    is driven by a blend of luma and max-channel (the same trick the bloom's
 *    bright pass uses) so a dark saturated violet counts as a highlight and
 *    keeps every bit of its chroma, while the felt still counts as shadow.
 *
 * `intensity` is a straight mix against the ungraded image, and 0 is a real
 * early-out — the shader returns before any of the maths above.
 *
 * ## Why the defocus lives here
 *
 * A separate depth-of-field pass would cost a second full-resolution read and
 * write for the sake of one `mix()`. This is already the last pass in the
 * chain, so the composite half of the defocus rides along inside it: when
 * `focusStrength` is 0 — the common case, and the case during all normal play —
 * nothing extra happens at all, not even a texture fetch. When it is not, two
 * half-resolution blur passes run first and the result is mixed in under a soft
 * radial mask before the grade sees it. See `@/pixi/postfx` for the eased
 * `setFocus()` / `clearFocus()` API that drives it.
 */

import { Filter, GlProgram, Texture, TexturePool, defaultFilterVert } from 'pixi.js';
import type { FilterSystem, RenderSurface } from 'pixi.js';

import { LIGHT } from '@/pixi/layout';

import { GLSL_COLOUR, GLSL_FRAME, GLSL_GLOBALS, mixRgb, rgb01, vec3f } from './glsl';

/** Below this the defocus is skipped entirely — no blur passes, no fetch. */
const FOCUS_OFF_EPSILON = 0.002;

/**
 * 5-tap binomial kernel [1,4,6,4,1]/16 collapsed onto three linear-filtered
 * fetches. Enough for a 1–2px defocus; a wider kernel would be spending
 * bandwidth on a blur nobody is meant to notice.
 */
const BLUR_OFFSET = 1.2;
const BLUR_WEIGHT_0 = 0.375;
const BLUR_WEIGHT_1 = 0.3125;

const blurFragment = /* glsl */ `
in vec2 vTextureCoord;
out vec4 finalColor;

uniform sampler2D uTexture;
${GLSL_GLOBALS}
uniform vec2 uStep;

void main(void) {
    // uStep is in *input* pixels, so the same uniform means the same physical
    // radius whether this pass is reading the full-resolution frame or the
    // half-resolution intermediate.
    vec2 d = uStep * uInputPixel.zw;

    vec4 acc = texture(uTexture, vTextureCoord) * ${BLUR_WEIGHT_0.toFixed(6)};
    acc += texture(uTexture, clamp(vTextureCoord + d * ${BLUR_OFFSET.toFixed(6)}, uInputClamp.xy, uInputClamp.zw)) * ${BLUR_WEIGHT_1.toFixed(6)};
    acc += texture(uTexture, clamp(vTextureCoord - d * ${BLUR_OFFSET.toFixed(6)}, uInputClamp.xy, uInputClamp.zw)) * ${BLUR_WEIGHT_1.toFixed(6)};

    finalColor = acc;
}
`;

const gradeFragment = /* glsl */ `
in vec2 vTextureCoord;
out vec4 finalColor;

uniform sampler2D uTexture;
uniform sampler2D uBlurTexture;
${GLSL_GLOBALS}

uniform float uIntensity;
uniform float uExposure;
uniform float uShoulder;
uniform float uContrast;
uniform float uLift;
uniform vec3 uShadowTint;
uniform vec3 uHighlightTint;
uniform vec2 uSatRange;
uniform vec2 uBleach;
uniform float uBleachSat;
uniform float uBleachWhite;
uniform float uSatBias;
uniform vec2 uSat;

uniform vec4 uFocus;
uniform float uFocusStrength;
uniform vec2 uBlurScale;
uniform vec4 uBlurClamp;

${GLSL_COLOUR}
${GLSL_FRAME}

vec3 tonemap(vec3 c) {
    // Display -> pseudo-linear. Gamma 2.0 rather than a real sRGB decode: two
    // multiplies instead of a pow, and the difference at this subtlety is far
    // below one 8-bit step.
    vec3 lin = c * c * uExposure;

    // Extended Reinhard. Monotonic, slope 1 at black so nothing in the deep
    // felt is touched, and uShoulder maps to exactly 1.0 — that is the rolloff.
    float w2 = max(uShoulder * uShoulder, 1e-4);
    vec3 tone = lin * (1.0 + lin / w2) / (1.0 + lin);

    return sqrt(max(tone, vec3(0.0)));
}

void main(void) {
    vec4 src = texture(uTexture, vTextureCoord);

    if (uFocusStrength > 0.0) {
        // Aspect-corrected distance from the pile being resolved, in units of
        // frame height, so the sharp region is a circle and not an ellipse.
        vec2 p = (frameUv(vTextureCoord) - uFocus.xy) * vec2(frameAspect(), 1.0);
        float mask = smoothstep(uFocus.z, uFocus.z + max(uFocus.w, 1e-4), length(p));

        vec4 blurred = texture(uBlurTexture, clamp(vTextureCoord * uBlurScale, uBlurClamp.xy, uBlurClamp.zw));
        // Premultiplied colours interpolate linearly, so this mix is legal
        // without unpacking either side first.
        src = mix(src, blurred, mask * uFocusStrength);
    }

    if (uIntensity <= 0.0) {
        finalColor = src;
        return;
    }

    vec3 base = unpremultiply(src);
    vec3 c = tonemap(base);

    // Toe and shoulder in display space. Blending toward smoothstep keeps the
    // curve anchored at 0 and 1 and leaves slope (1 - uContrast) at black,
    // which is what stops the felt from crushing.
    c = mix(c, c * c * (3.0 - 2.0 * c), uContrast);

    // A trace of print black, so nothing in frame is ever a dead zero.
    c = uLift + (1.0 - uLift) * c;

    // One brightness metric for both the split tone and the saturation ramp.
    float b = mix(luma(c), maxChannel(c), uSatBias);
    float w = smoothstep(uSatRange.x, uSatRange.y, b);

    c *= mix(uShadowTint, uHighlightTint, w);

    // Highlight bleach.
    //
    // Film and sensors both desaturate toward white as exposure climbs: the
    // channels clip in sequence, and that bleach at the top of the curve is why
    // a real highlight reads as *light* rather than as a bright coloured thing.
    // The saturation ramp above runs the other way — it pushes chroma UP in the
    // highlights — so a gold flare core arrived saturated and got saturated
    // further, driving it toward pure yellow. This pulls it back.
    //
    // Keyed high (0.72..0.97) so mid-tone gold on a card face is untouched and
    // only genuine flare cores are affected.
    float bleach = smoothstep(uBleach.x, uBleach.y, b);

    float l = luma(c);
    c = mix(vec3(l), c, mix(uSat.x, uSat.y, w) * mix(1.0, uBleachSat, bleach));
    c = mix(c, vec3(1.0), bleach * uBleachWhite);

    finalColor = premultiply(mix(base, c, uIntensity), src.a);
}
`;

export interface ColourGradeFilterOptions {
  /** Master mix against the ungraded frame. 0 is a true early-out. */
  intensity?: number;
  /** Linear exposure applied before the curve. */
  exposure?: number;
  /** Display value that the highlight rolloff maps to 1.0. Higher = softer. */
  shoulder?: number;
  /** S-curve strength, 0..1. Slope at black is `1 - contrast`. */
  contrast?: number;
  /** Print black. A trace, so nothing in frame is a dead zero. */
  lift?: number;
  /** Colour the shadows are toned toward. Defaults to the fill light. */
  shadowTint?: number;
  /** How far toward `shadowTint` the shadows go. */
  shadowTintAmount?: number;
  /** Colour the highlights are toned toward. Defaults to the key light. */
  highlightTint?: number;
  /** How far toward `highlightTint` the highlights go. */
  highlightTintAmount?: number;
  /** Saturation multiplier in the deep shadows. Below 1 declutters the felt. */
  /**
   * Brightness range over which the highlight bleach ramps in, 0..1.
   *
   * Keyed high so only genuine flare cores desaturate; mid-tone colour on a
   * card face must not be touched.
   */
  bleachLow?: number;
  bleachHigh?: number;
  /** Saturation multiplier at full bleach. Below 1 pulls chroma out of flares. */
  bleachSaturation?: number;
  /** How far a fully bleached highlight lifts toward white, 0..1. */
  bleachWhite?: number;
  shadowSaturation?: number;
  /** Saturation multiplier in the highlights. */
  highlightSaturation?: number;
  /** Brightness at which the shadow end of the ramp stops. */
  saturationLow?: number;
  /** Brightness at which the highlight end of the ramp starts. */
  saturationHigh?: number;
  /** 0 = pure luma ramp, 1 = pure max-channel. Lets saturated darks read high. */
  saturationBias?: number;
  /** Defocus blur radius, in device pixels of the incoming frame. */
  blurRadius?: number;
  /** Resolution divisor for the defocus blur targets. 2 = half resolution. */
  blurDownsample?: number;
}

/**
 * Tuned against the palette rather than by eye: see the numbers in the header.
 * The whole set is deliberately mild, so `intensity` is a dial *down* from a
 * defensible look rather than up from nothing.
 */
const DEFAULTS: Required<Omit<ColourGradeFilterOptions, 'shadowTint' | 'highlightTint'>> = {
  intensity: 1,
  exposure: 1.06,
  // Lowered from 1.18. The bloom composite now applies its own soft shoulder
  // BEFORE the grade sees the frame, so two curves at the old strength would
  // compound and flatten the top end twice.
  shoulder: 1.06,
  contrast: 0.17,
  lift: 0.005,
  shadowTintAmount: 0.09,
  highlightTintAmount: 0.16,
  bleachLow: 0.72,
  bleachHigh: 0.97,
  bleachSaturation: 0.55,
  bleachWhite: 0.18,
  shadowSaturation: 0.9,
  highlightSaturation: 1.04,
  saturationLow: 0.06,
  saturationHigh: 0.5,
  saturationBias: 0.5,
  blurRadius: 1.6,
  blurDownsample: 2,
};

/** Rec.709, matching `luma()` in the shared GLSL. */
function relativeLuma(rgb: readonly [number, number, number]): number {
  return 0.2126 * rgb[0] + 0.7152 * rgb[1] + 0.0722 * rgb[2];
}

/**
 * A tone as a *multiplier*: normalised to luma 1 so it shifts hue without
 * shifting exposure, then mixed back toward white by `amount`.
 */
function tintMultiplier(hex: number, amount: number): [number, number, number] {
  const rgb = rgb01(hex);
  const l = relativeLuma(rgb);
  const normalised: [number, number, number] =
    l > 1e-4 ? [rgb[0] / l, rgb[1] / l, rgb[2] / l] : [1, 1, 1];
  return mixRgb([1, 1, 1], normalised, Math.max(0, amount));
}

interface GradeUniforms {
  uIntensity: number;
  uExposure: number;
  uShoulder: number;
  uContrast: number;
  uLift: number;
  uShadowTint: Float32Array;
  uHighlightTint: Float32Array;
  uSatRange: Float32Array;
  uBleach: Float32Array;
  uBleachSat: number;
  uBleachWhite: number;
  uSatBias: number;
  uSat: Float32Array;
  uFocus: Float32Array;
  uFocusStrength: number;
  uBlurScale: Float32Array;
  uBlurClamp: Float32Array;
}

interface BlurUniforms {
  uStep: Float32Array;
}

export class ColourGradeFilter extends Filter {
  /** Separable 3-fetch Gaussian, re-used for both axes of the defocus. */
  readonly #blur: Filter;

  readonly #uniforms: GradeUniforms;
  readonly #blurUniforms: BlurUniforms;

  #shadowTintHex: number;
  #highlightTintHex: number;
  #shadowTintAmount: number;
  #highlightTintAmount: number;
  #blurRadius: number;
  #blurDownsample: number;

  constructor(options: ColourGradeFilterOptions = {}) {
    const o = { ...DEFAULTS, ...options };
    const shadowHex = options.shadowTint ?? LIGHT.fill.colour;
    const highlightHex = options.highlightTint ?? LIGHT.colour;

    super({
      glProgram: GlProgram.from({
        vertex: defaultFilterVert,
        fragment: gradeFragment,
        name: 'wild21-colour-grade',
        preferredFragmentPrecision: 'highp',
      }),
      resources: {
        uBlurTexture: Texture.EMPTY.source,
        gradeUniforms: {
          uIntensity: { value: o.intensity, type: 'f32' },
          uExposure: { value: o.exposure, type: 'f32' },
          uShoulder: { value: o.shoulder, type: 'f32' },
          uContrast: { value: o.contrast, type: 'f32' },
          uLift: { value: o.lift, type: 'f32' },
          uShadowTint: {
            value: vec3f(tintMultiplier(shadowHex, o.shadowTintAmount)),
            type: 'vec3<f32>',
          },
          uHighlightTint: {
            value: vec3f(tintMultiplier(highlightHex, o.highlightTintAmount)),
            type: 'vec3<f32>',
          },
          uSatRange: {
            value: new Float32Array([o.saturationLow, o.saturationHigh]),
            type: 'vec2<f32>',
          },
          uSatBias: { value: o.saturationBias, type: 'f32' },
          uBleach: {
            value: new Float32Array([o.bleachLow, o.bleachHigh]),
            type: 'vec2<f32>',
          },
          uBleachSat: { value: o.bleachSaturation, type: 'f32' },
          uBleachWhite: { value: o.bleachWhite, type: 'f32' },
          uSat: {
            value: new Float32Array([o.shadowSaturation, o.highlightSaturation]),
            type: 'vec2<f32>',
          },
          uFocus: { value: new Float32Array([0.5, 0.5, 0.5, 0.25]), type: 'vec4<f32>' },
          uFocusStrength: { value: 0, type: 'f32' },
          uBlurScale: { value: new Float32Array([1, 1]), type: 'vec2<f32>' },
          uBlurClamp: { value: new Float32Array([0, 0, 1, 1]), type: 'vec4<f32>' },
        },
      },
      resolution: 'inherit',
      antialias: 'inherit',
      padding: 0,
    });

    this.#uniforms = this.resources.gradeUniforms.uniforms as GradeUniforms;

    this.#blur = new Filter({
      glProgram: GlProgram.from({
        vertex: defaultFilterVert,
        fragment: blurFragment,
        name: 'wild21-defocus-blur',
        preferredFragmentPrecision: 'highp',
      }),
      resources: {
        blurUniforms: {
          uStep: { value: new Float32Array([1, 0]), type: 'vec2<f32>' },
        },
      },
      resolution: 'inherit',
      antialias: 'off',
    });
    this.#blurUniforms = this.#blur.resources.blurUniforms.uniforms as BlurUniforms;

    this.#shadowTintHex = shadowHex;
    this.#highlightTintHex = highlightHex;
    this.#shadowTintAmount = o.shadowTintAmount;
    this.#highlightTintAmount = o.highlightTintAmount;
    this.#blurRadius = Math.max(0, o.blurRadius);
    this.#blurDownsample = Math.max(1, o.blurDownsample);
  }

  /** Master mix against the ungraded frame. 0 skips the grade entirely. */
  get intensity(): number {
    return this.#uniforms.uIntensity;
  }
  set intensity(value: number) {
    this.#uniforms.uIntensity = Math.min(1, Math.max(0, value));
  }

  /** Linear exposure applied before the curve. */
  get exposure(): number {
    return this.#uniforms.uExposure;
  }
  set exposure(value: number) {
    this.#uniforms.uExposure = Math.max(0, value);
  }

  /** Display value the highlight rolloff maps to 1.0. Higher = softer. */
  get shoulder(): number {
    return this.#uniforms.uShoulder;
  }
  set shoulder(value: number) {
    this.#uniforms.uShoulder = Math.max(1, value);
  }

  /** S-curve strength. Slope at black is `1 - contrast`, so keep it well under 1. */
  get contrast(): number {
    return this.#uniforms.uContrast;
  }
  set contrast(value: number) {
    this.#uniforms.uContrast = Math.min(0.9, Math.max(0, value));
  }

  /** Print black. */
  get lift(): number {
    return this.#uniforms.uLift;
  }
  set lift(value: number) {
    this.#uniforms.uLift = Math.min(0.1, Math.max(0, value));
  }

  /** Re-tone the shadows. Accepts a 0xRRGGBB token value. */
  setShadowTint(hex: number = this.#shadowTintHex, amount: number = this.#shadowTintAmount): void {
    this.#shadowTintHex = hex;
    this.#shadowTintAmount = amount;
    this.#writeVec3(this.#uniforms.uShadowTint, tintMultiplier(hex, amount));
  }

  /** Re-tone the highlights. Accepts a 0xRRGGBB token value. */
  setHighlightTint(
    hex: number = this.#highlightTintHex,
    amount: number = this.#highlightTintAmount,
  ): void {
    this.#highlightTintHex = hex;
    this.#highlightTintAmount = amount;
    this.#writeVec3(this.#uniforms.uHighlightTint, tintMultiplier(hex, amount));
  }

  /** How far the shadows are toned toward `shadowTint`. */
  get shadowTintAmount(): number {
    return this.#shadowTintAmount;
  }
  set shadowTintAmount(value: number) {
    this.setShadowTint(this.#shadowTintHex, value);
  }

  /** How far the highlights are toned toward `highlightTint`. */
  get highlightTintAmount(): number {
    return this.#highlightTintAmount;
  }
  set highlightTintAmount(value: number) {
    this.setHighlightTint(this.#highlightTintHex, value);
  }

  /** Saturation multiplier in the deep shadows. */
  get shadowSaturation(): number {
    return this.#uniforms.uSat[0];
  }
  set shadowSaturation(value: number) {
    this.#uniforms.uSat[0] = Math.max(0, value);
  }

  /** Saturation multiplier in the highlights. */
  get highlightSaturation(): number {
    return this.#uniforms.uSat[1];
  }
  set highlightSaturation(value: number) {
    this.#uniforms.uSat[1] = Math.max(0, value);
  }

  /** Brightness window the shadow-to-highlight ramp crosses. */
  setSaturationRange(low: number, high: number): void {
    const range = this.#uniforms.uSatRange;
    range[0] = Math.max(0, low);
    range[1] = Math.max(low + 1e-3, high);
  }

  /** 0 = pure luma ramp, 1 = pure max-channel. */
  get saturationBias(): number {
    return this.#uniforms.uSatBias;
  }
  set saturationBias(value: number) {
    this.#uniforms.uSatBias = Math.min(1, Math.max(0, value));
  }

  /**
   * Defocus blur radius, in device pixels of the incoming frame. The brief is
   * 1–2px; past that it stops reading as a lens and starts reading as a bug.
   */
  get blurRadius(): number {
    return this.#blurRadius;
  }
  set blurRadius(value: number) {
    this.#blurRadius = Math.max(0, value);
  }

  /** Resolution divisor for the defocus targets. 2 = half resolution. */
  get blurDownsample(): number {
    return this.#blurDownsample;
  }
  set blurDownsample(value: number) {
    this.#blurDownsample = Math.max(1, value);
  }

  /**
   * Point the defocus at something, in **frame** units — 0..1 across the
   * filtered area for the centre, and units of frame *height* for the radius
   * and the feather, so the sharp region stays circular at every aspect ratio.
   * `@/pixi/postfx` owns the conversion from design units and the easing.
   *
   * @param strength - 0 disables the whole defocus, blur passes included.
   */
  setFocus(u: number, v: number, radius: number, feather: number, strength: number): void {
    const focus = this.#uniforms.uFocus;
    focus[0] = u;
    focus[1] = v;
    focus[2] = Math.max(0, radius);
    focus[3] = Math.max(1e-4, feather);
    this.#uniforms.uFocusStrength = Math.min(1, Math.max(0, strength));
  }

  /** Current defocus strength. 0 means the blur passes do not run at all. */
  get focusStrength(): number {
    return this.#uniforms.uFocusStrength;
  }
  set focusStrength(value: number) {
    this.#uniforms.uFocusStrength = Math.min(1, Math.max(0, value));
  }

  override apply(
    filterManager: FilterSystem,
    input: Texture,
    output: RenderSurface,
    clearMode: boolean,
  ): void {
    // The common case, and every frame of normal play: no resolve is happening,
    // so there is no blur to build and the shader never touches uBlurTexture.
    if (this.#uniforms.uFocusStrength <= FOCUS_OFF_EPSILON || this.#blurRadius <= 0) {
      this.resources.uBlurTexture = Texture.EMPTY.source;
      filterManager.applyFilter(this, input, output, clearMode);
      return;
    }

    const frameWidth = input.frame.width;
    const frameHeight = input.frame.height;
    const targetResolution = input.source.resolution / this.#blurDownsample;

    // Same logical frame, fewer pixels — the filter quad maths stays exact.
    const front = TexturePool.getOptimalTexture(frameWidth, frameHeight, targetResolution, false);
    const back = TexturePool.getOptimalTexture(frameWidth, frameHeight, targetResolution, false);

    const step = this.#blurUniforms.uStep;

    // Horizontal, reading the full-resolution frame into the reduced target.
    step[0] = this.#blurRadius;
    step[1] = 0;
    filterManager.applyFilter(this.#blur, input, front, true);

    // Vertical, reading the reduced target — whose texels are `downsample`
    // device pixels wide, hence the divide.
    step[0] = 0;
    step[1] = this.#blurRadius / this.#blurDownsample;
    filterManager.applyFilter(this.#blur, front, back, true);

    const blurSource = back.source;
    const scale = this.#uniforms.uBlurScale;
    scale[0] = input.source.width / blurSource.width;
    scale[1] = input.source.height / blurSource.height;

    const halfTexelX = 0.5 / blurSource.pixelWidth;
    const halfTexelY = 0.5 / blurSource.pixelHeight;
    const clampUv = this.#uniforms.uBlurClamp;
    clampUv[0] = halfTexelX;
    clampUv[1] = halfTexelY;
    clampUv[2] = back.frame.width / blurSource.width - halfTexelX;
    clampUv[3] = back.frame.height / blurSource.height - halfTexelY;

    this.resources.uBlurTexture = blurSource;
    filterManager.applyFilter(this, input, output, clearMode);

    this.resources.uBlurTexture = Texture.EMPTY.source;
    TexturePool.returnTexture(front);
    TexturePool.returnTexture(back);
  }

  override destroy(): void {
    this.#blur.destroy();
    super.destroy();
  }

  #writeVec3(target: Float32Array, rgb: readonly [number, number, number]): void {
    target[0] = rgb[0];
    target[1] = rgb[1];
    target[2] = rgb[2];
  }
}
